import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptorFn,
  HttpErrorResponse,
  HttpResponse
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map, finalize } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { inject } from '@angular/core';
import { AUTH_CONSTANTS, API_CONSTANTS } from '../shared/constants';

// Helper function to check if a request should skip token addition
function shouldSkipAuth(url: string): boolean {
  console.log(`%c [AUTH FLOW] Checking if URL should skip auth: ${url}`, AUTH_CONSTANTS.LOG_STYLES.AUTH);
  
  // Check if the URL includes any of the auth endpoints
  const shouldSkip = Object.values(API_CONSTANTS.AUTH_ENDPOINTS).some(endpoint => {
    // Special handling for candidate booking URLs that have dynamic tokens
    if (endpoint === '/api/candidate/booking') {
      return url.includes('/api/candidate/booking/');
    }
    return url.includes(endpoint);
  });
  
  console.log(`%c [AUTH FLOW] Should skip auth: ${shouldSkip}`, AUTH_CONSTANTS.LOG_STYLES.AUTH);
  return shouldSkip;
}

// Helper function to add token to request
function addTokenToRequest(request: HttpRequest<any>, token: string): HttpRequest<any> {
  // Simplified logging - just one log statement
  console.log(`%c [AUTH FLOW] Adding token to request: ${request.url}`, AUTH_CONSTANTS.LOG_STYLES.TOKEN);
  
  // Clone the request with the authorization header
  const clonedReq = request.clone({
    setHeaders: {
      Authorization: `Bearer ${token}`
    }
  });
  
  return clonedReq;
}

// Helper function to check for renewed token in response
function handleRenewedToken(event: HttpEvent<any>, authService: AuthService): HttpEvent<any> {
  if (event instanceof HttpResponse) {
    // Check if there's a renewed token header
    const renewedToken = event.headers.get('X-Renewed-Token');
    
    if (null !== renewedToken) {
      // Only log when we actually find a renewed token
      console.log('%c [AUTH FLOW] Received renewed token from server', AUTH_CONSTANTS.LOG_STYLES.INFO);
      // Update the token in the auth service
      authService.updateToken(renewedToken);
    }
  }
  return event;
}

/**
 * Functional interceptor for handling authentication tokens
 * 
 * This interceptor:
 * 1. Adds the JWT token to outgoing requests
 * 2. Handles token renewal from response headers
 * 3. Handles 401 Unauthorized errors by logging out the user
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  // Get the auth service using inject
  const authService = inject(AuthService);
  
  // Create a unique ID for this request to track it through the flow
  const requestId = Math.random().toString(36).substring(2, 8);
  
  // Minimal logging for request start
  console.log(`%c [AUTH FLOW ${requestId}] Request to: ${req.url}`, AUTH_CONSTANTS.LOG_STYLES.INFO);
  
  // Don't add token for auth endpoints
  if (shouldSkipAuth(req.url)) {
    console.log(`%c [AUTH FLOW ${requestId}] Skipping auth for endpoint: ${req.url}`, AUTH_CONSTANTS.LOG_STYLES.WARNING);
    return next(req);
  }

  // Add auth token to request if available - use silent mode to avoid excessive logging
  const token = authService.getToken(true);
  
  // Only log when token is missing
  if (!token) {
    console.log(`%c [AUTH FLOW ${requestId}] No token available for: ${req.url}`, AUTH_CONSTANTS.LOG_STYLES.ERROR);
  }
  
  // Check if we're accessing admin endpoints - only log for admin requests
  const isAdminRequest = req.url.includes('/admin/');
  if (isAdminRequest) {
    console.log(`%c [AUTH FLOW ${requestId}] Admin request detected: ${req.url}`, AUTH_CONSTANTS.LOG_STYLES.ADMIN);
  }
  
  if (token) {
    // Clone the request and add the authorization header
    const authReq = addTokenToRequest(req, token);
    
    // Pass the cloned request to the next handler
    return next(authReq).pipe(
      map(event => {
        console.log(`%c [AUTH FLOW ${requestId}] 📥 RESPONSE RECEIVED`, 'background: #4682B4; color: #ffffff; font-size: 16px; padding: 10px;');
        if (event instanceof HttpResponse) {
          console.log(`%c [AUTH FLOW ${requestId}] Response status:`, 'color: #4682B4; font-weight: bold;', event.status);
          console.log(`%c [AUTH FLOW ${requestId}] Response headers:`, 'color: #4682B4; font-weight: bold;', event.headers.keys());
        }
        return handleRenewedToken(event, authService);
      }),
      catchError((error: HttpErrorResponse) => {
        console.log(`%c [AUTH FLOW ${requestId}] ❌ ERROR OCCURRED ❌`, 'background: #FF0000; color: #ffffff; font-size: 16px; padding: 10px;');
        console.log(`%c [AUTH FLOW ${requestId}] Error status:`, 'color: #FF0000; font-weight: bold;', error.status);
        console.log(`%c [AUTH FLOW ${requestId}] Error message:`, 'color: #FF0000; font-weight: bold;', error.message);
        console.log(`%c [AUTH FLOW ${requestId}] Full error:`, 'color: #FF0000;', error);
        
        if (401 === error.status) {
          console.log(`%c [AUTH FLOW ${requestId}] 🔒 401 UNAUTHORIZED - LOGGING OUT 🔒`, 'background: #FF0000; color: #ffffff; font-size: 16px; padding: 10px;');
          // Handle 401 Unauthorized error - session expired
          authService.logout();
        }
        
        return throwError(() => error);
      })
    );
  }
  
  // No token available, just pass the request through
  return next(req);
};
